import { apiFetch } from './client';
import { config } from '../config';

// Block types — mirror internal/services/broadcasts/types.go constants.
export const BROADCAST_BLOCK_TYPES = ['title', 'subtitle', 'body', 'image'] as const;
export type BroadcastBlockType = (typeof BROADCAST_BLOCK_TYPES)[number];

export interface BroadcastBlock {
  type: BroadcastBlockType;
  text?: string;
  // object_key references an org-uploaded image (rendered via /storage/<key>).
  object_key?: string;
  // image_url is an absolute URL for an image not in org storage — e.g. the
  // stock photo baked into a seed-imported broadcast. object_key wins when
  // both are set (an upload supersedes the seeded stock photo).
  image_url?: string;
  // width is the image's display width as a percentage of the email content
  // column (10–100). Omitted / 0 means full width. Height always follows the
  // source aspect ratio — there is no independent height. Mirrors
  // broadcasts.Block.Width; clamping rules live in imageWidthPct().
  width?: number;
}

// Audience — union semantics. all_members short-circuits the other two
// (when true, the team / member sub-pickers are hidden in the UI).
export interface BroadcastAudience {
  all_members?: boolean;
  team_ids?: string[];
  member_ids?: string[];
}

export type BroadcastStatus = 'draft' | 'sent';

// BroadcastTranslation is one language variant of a broadcast's public-site
// content. subject + blocks mirror the source-language fields; the public
// renderer substitutes them for visitors viewing the site in that language.
export interface BroadcastTranslation {
  subject: string;
  blocks: BroadcastBlock[];
}

export interface Broadcast {
  id: string;
  subject: string;
  blocks: BroadcastBlock[];
  audience: BroadcastAudience;
  status: BroadcastStatus;
  // scheduled_at is an RFC3339 timestamp when the draft is scheduled for
  // automatic sending by the worker; absent on plain drafts and sent rows.
  // Only meaningful while status === 'draft'.
  scheduled_at?: string;
  sent_at?: string;
  sent_count: number;
  failed_count: number;
  publish_to_website: boolean;
  slug: string;
  imported_from_seed: boolean;
  // Per-broadcast toggles for the club-branded header (logo + name)
  // and footer (attribution + unsubscribe). Default TRUE on the server;
  // the editor lets authors opt out per row.
  include_header: boolean;
  include_footer: boolean;
  // Multilingual public-site content. source_language is the language the
  // canonical subject + blocks are written in; translations holds the
  // per-language variants keyed by language code. Empty = single-language.
  source_language: string;
  translations: Record<string, BroadcastTranslation>;
  created_at: string;
  updated_at: string;
}

export interface ListBroadcastsResponse {
  broadcasts: Broadcast[];
}

export interface AudiencePreview {
  member_count: number;
  email_count: number;
}

export interface BroadcastSavePayload {
  subject: string;
  blocks: BroadcastBlock[];
  audience: BroadcastAudience;
  publish_to_website: boolean;
  include_header: boolean;
  include_footer: boolean;
  // Multilingual public-site content. Omitted on single-language broadcasts;
  // the server defaults source_language and treats absent translations as {}.
  source_language?: string;
  translations?: Record<string, BroadcastTranslation>;
  // scheduled_at, when set (RFC3339), marks the draft for automatic sending
  // at that time. Send null / empty to clear an existing schedule. The server
  // rejects a past time or an empty audience with no website publish.
  scheduled_at?: string | null;
}

// --- Wire ---------------------------------------------------------------

export async function listBroadcasts(
  token: string | null,
  slug: string,
): Promise<Broadcast[]> {
  const res = await apiFetch<ListBroadcastsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts`,
    { token },
  );
  return res.broadcasts ?? [];
}

export async function getBroadcast(
  token: string | null,
  slug: string,
  id: string,
): Promise<Broadcast> {
  return apiFetch<Broadcast>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts/${encodeURIComponent(id)}`,
    { token },
  );
}

export async function createBroadcast(
  token: string | null,
  slug: string,
  body: BroadcastSavePayload,
): Promise<Broadcast> {
  return apiFetch<Broadcast>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts`,
    { token, method: 'POST', body },
  );
}

export async function updateBroadcast(
  token: string | null,
  slug: string,
  id: string,
  body: BroadcastSavePayload,
): Promise<Broadcast> {
  return apiFetch<Broadcast>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts/${encodeURIComponent(id)}`,
    { token, method: 'PUT', body },
  );
}

export async function deleteBroadcast(
  token: string | null,
  slug: string,
  id: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts/${encodeURIComponent(id)}`,
    { token, method: 'DELETE' },
  );
}

export async function sendBroadcast(
  token: string | null,
  slug: string,
  id: string,
): Promise<{ status: string }> {
  return apiFetch<{ status: string }>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts/${encodeURIComponent(id)}/send`,
    { token, method: 'POST', body: {} },
  );
}

export interface QuickEmailPayload {
  subject: string;
  body: string;
  member_ids: string[];
}

// quickSendMemberEmail powers the Members page Actions → email flow: a short
// subject + free-text message sent to the selected members, wrapped in the
// club's branded header/footer. Fire-and-forget — the server returns 202 and
// reports progress on the notification bell. No broadcast row is created.
export async function quickSendMemberEmail(
  token: string | null,
  slug: string,
  body: QuickEmailPayload,
): Promise<{ status: string }> {
  return apiFetch<{ status: string }>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts/quick-send`,
    { token, method: 'POST', body },
  );
}

// setBroadcastWebsite flips the publish-to-website flag. Unlike updateBroadcast
// this is allowed on a SENT broadcast — the email is immutable, but whether it
// also appears as a public news article is reversible. Returns the updated row.
export async function setBroadcastWebsite(
  token: string | null,
  slug: string,
  id: string,
  publish: boolean,
): Promise<Broadcast> {
  return apiFetch<Broadcast>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts/${encodeURIComponent(id)}/website`,
    { token, method: 'PUT', body: { publish } },
  );
}

export async function previewAudience(
  token: string | null,
  slug: string,
  audience: BroadcastAudience,
): Promise<AudiencePreview> {
  return apiFetch<AudiencePreview>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts/audience-preview`,
    { token, method: 'POST', body: { audience } },
  );
}

export interface BroadcastDraftRequest {
  brief: string;
  // ISO-639-1 UI locale so the model drafts in the admin's language.
  locale: string;
  // When true, the model suggests a fitting stock-photo category which the
  // backend resolves to a leading image block (a first-party CDN URL).
  want_stock_image: boolean;
}

export interface BroadcastDraftResponse {
  subject: string;
  blocks: BroadcastBlock[];
}

// generateBroadcastDraft asks the AI for a proposed subject + body blocks
// from a short brief. It NEVER writes — the SPA hydrates the result into the
// editor where the admin reviews, picks recipients, and sends.
export async function generateBroadcastDraft(
  token: string | null,
  slug: string,
  body: BroadcastDraftRequest,
): Promise<BroadcastDraftResponse> {
  return apiFetch<BroadcastDraftResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/ai/broadcast-draft`,
    { token, method: 'POST', body },
  );
}

// uploadBroadcastImage POSTs a multipart form with field name `image`
// and returns the storage object_key the Image block stores.
// Bypasses apiFetch because that helper JSON-stringifies any body —
// multipart needs the browser to set Content-Type with boundary itself.
export async function uploadBroadcastImage(
  token: string | null,
  slug: string,
  file: File,
): Promise<{ object_key: string; content_type: string; size: number }> {
  const form = new FormData();
  form.append('image', file);
  const res = await fetch(
    `${config.apiURL}/api/v1/organizations/${encodeURIComponent(slug)}/broadcasts/upload-image`,
    {
      method: 'POST',
      body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!res.ok) {
    throw new Error(`broadcast image upload failed: ${res.status}`);
  }
  return res.json();
}
